import { init as initFullStory } from "@fullstory/browser";
import { StyledEngineProvider, ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import * as Sentry from "@sentry/react";
import ErrorPage from "_shared/_errors/_views/error-page";
import ConfirmDialogProvider from "_shared/_providers/dialogs/confirm/confirm-dialog";
import DialogProvider from "_shared/_providers/dialogs/dialog";
import ImagePreviewDialogProvider from "_shared/_providers/dialogs/image-preview/image-preview-dialog";
import attorneyHubStore from "_shared/_stores/redux-store";
import { ENVIRONMENT, IS_DEV } from "_shared/_utilities/meta";
import Dashboard from "_shared/_views/dashboards/dashboard";
import dashboardLoader from "_shared/_views/dashboards/dashboard-loader";
import invitationLoader from "_shared/_views/loaders/invitation";
import marketplaceLoader from "_shared/_views/loaders/marketplace";
import marketplaceProfileLoader from "_shared/_views/loaders/marketplace-profile";
import marketplaceResultProfileLoader from "_shared/_views/loaders/marketplace-result-profile";
import networkLoader from "_shared/_views/loaders/network";
import proposalsLoader from "_shared/_views/loaders/proposals";
import rootLoader from "_shared/_views/loaders/root";
import superUserLoader from "_shared/_views/loaders/super-users";
import teamsLoader from "_shared/_views/loaders/teams";
import workLoader from "_shared/_views/loaders/work";
import Main from "_shared/_views/main";
import Calendar from "_shared/dates/_views/calendar";
import Teams from "_shared/teams/teams-page";
import Contracts from "documents/types/contract-documents/_views/contracts-page";
import contractsLoader from "documents/types/contract-documents/_views/loaders/contracts-loader";
import "index.css";
import EntityAdministration from "legal-entities/administrators/_views/administration-page";
import entityAdministratorLoader from "legal-entities/administrators/_views/administrator-loader";
import EntityAgreementPage from "legal-entities/agreements/_views/entity-agreement-page";
import AdministratorInvitationPage from "legal-entities/invitations/administrator-invitations/_views/administrator-invitation-page";
import UserEntityMemberInvitationAcceptancePage from "legal-entities/invitations/member-invitations/email-based/_views/user-entity-member-invitation-acceptance-page";
import OpenEntityMemberInvitationPage from "legal-entities/invitations/member-invitations/open/_views/open-entity-member-invitation-acceptance-page";
import EntityOfficerInvitationPage from "legal-entities/invitations/officer-invitations/_views/entity-officer-invitation-page";
import MarketplacePage from "marketplace/_views/marketplace-page";
import ManageProfiles from "marketplace/_views/profiles/profiles-page";
import Bookmarks from "marketplace/bookmarks/_views/bookmarks-page";
import bookmarksLoader from "marketplace/bookmarks/_views/loaders/bookmarks-loader";
import TeamInvitationPage from "marketplace/teams/invitations/_views/team-invitation-page";
import CommunicationsPage from "notifications/_views/communications-page";
import communicationsLoader from "notifications/_views/loader/communications-loader";
import { SnackbarProvider } from "notistack";
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router-dom";
import { RequireSession, SessionProvider } from "users/__session/session-context";
import LoginPage from "users/auth/_views/login-page";
import Network from "users/networking/_views/network-page";
import SuperUserDashboard from "users/super-users/_views/super-user-administration-panel";
import FeeSchedulesPage from "work/fee-schedule-templates/_views/pages/fee-schedules-page";
import feeScheduleLoader from "work/fee-schedule-templates/_views/pages/loaders/fee-schedule-loader";
import ProposalsPage from "work/proposals/_views/proposals-page";
import WorkAgreementsPage from "work/work-agreements/_views/work-agreements-page";

initFullStory({
  orgId: "o-1M2PCX-na1",
  devMode: IS_DEV,
});

Sentry.init({
  dsn: "https://9b4b0f7cb45a485ea5dcef37b8da09d7@o1250895.ingest.sentry.io/6415866",
  environment: ENVIRONMENT,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1,
});

let theme = createTheme();
theme = responsiveFontSizes(theme);

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <DialogProvider>
        <Main />
      </DialogProvider>
    ),
    errorElement: <ErrorPage />,
    loader: rootLoader,
    children: [
      {
        errorElement: <ErrorPage />,
        children: [
          {
            path: "dashboard",
            element: (
              <RequireSession>
                <Dashboard />
              </RequireSession>
            ),
            loader: dashboardLoader,
          },
          {
            path: "communications/:tab/:action/:id",
            element: (
              <RequireSession>
                <CommunicationsPage />
              </RequireSession>
            ),
            loader: communicationsLoader,
          },
          {
            path: "communications/:tab/:action",
            element: (
              <RequireSession>
                <CommunicationsPage />
              </RequireSession>
            ),
            loader: communicationsLoader,
          },
          {
            path: "communications/:tab",
            element: (
              <RequireSession>
                <CommunicationsPage />
              </RequireSession>
            ),
            loader: communicationsLoader,
          },
          {
            index: true,
            path: "communications",
            element: (
              <RequireSession>
                <CommunicationsPage />
              </RequireSession>
            ),
            loader: communicationsLoader,
          },
          {
            path: "contracts/:tab",
            element: (
              <RequireSession>
                <Contracts />
              </RequireSession>
            ),
            loader: contractsLoader,
          },
          {
            index: true,
            path: "contracts",
            element: (
              <RequireSession>
                <Contracts />
              </RequireSession>
            ),
            loader: contractsLoader,
          },
          {
            path: "work/:pageTab/:action/:id/:tab",
            element: (
              <RequireSession>
                <WorkAgreementsPage />
              </RequireSession>
            ),
            loader: workLoader,
          },
          {
            path: "work/:pageTab/:action/:id",
            element: (
              <RequireSession>
                <WorkAgreementsPage />
              </RequireSession>
            ),
            loader: workLoader,
          },

          {
            path: "work/:pageTab/:action",
            element: (
              <RequireSession>
                <WorkAgreementsPage />
              </RequireSession>
            ),
            loader: workLoader,
          },
          {
            path: "work/:pageTab",
            element: (
              <RequireSession>
                <WorkAgreementsPage />
              </RequireSession>
            ),
            loader: workLoader,
          },
          {
            index: true,
            path: "work",
            element: (
              <RequireSession>
                <WorkAgreementsPage />
              </RequireSession>
            ),
            loader: workLoader,
          },
          {
            path: "proposals/:pageTab/:action/:id/:tab",
            element: (
              <RequireSession>
                <ProposalsPage />
              </RequireSession>
            ),
            loader: proposalsLoader,
          },
          {
            path: "proposals/:pageTab/:action/:id",
            element: (
              <RequireSession>
                <ProposalsPage />
              </RequireSession>
            ),
            loader: proposalsLoader,
          },
          {
            path: "proposals/:pageTab/:action",
            element: (
              <RequireSession>
                <ProposalsPage />
              </RequireSession>
            ),
            loader: proposalsLoader,
          },
          {
            path: "proposals/:pageTab",
            element: (
              <RequireSession>
                <ProposalsPage />
              </RequireSession>
            ),
            loader: proposalsLoader,
          },
          {
            index: true,
            path: "proposals",
            element: (
              <RequireSession>
                <ProposalsPage />
              </RequireSession>
            ),
            loader: proposalsLoader,
          },
          {
            path: "teams/:tab/:action/:id",
            element: (
              <RequireSession>
                <Teams />
              </RequireSession>
            ),
            loader: teamsLoader,
          },
          {
            path: "teams/:tab/:action",
            element: (
              <RequireSession>
                <Teams />
              </RequireSession>
            ),
            loader: teamsLoader,
          },
          {
            path: "teams/:tab",
            element: (
              <RequireSession>
                <Teams />
              </RequireSession>
            ),
            loader: teamsLoader,
          },
          {
            index: true,
            path: "teams",
            element: (
              <RequireSession>
                <Teams />
              </RequireSession>
            ),
            loader: teamsLoader,
          },
          {
            path: "fee-schedules/:tab/:action/:id",
            element: (
              <RequireSession>
                <FeeSchedulesPage />
              </RequireSession>
            ),
            loader: feeScheduleLoader,
          },
          {
            path: "fee-schedules/:tab/:action",
            element: (
              <RequireSession>
                <FeeSchedulesPage />
              </RequireSession>
            ),
            loader: feeScheduleLoader,
          },
          {
            path: "fee-schedules/:tab",
            element: (
              <RequireSession>
                <FeeSchedulesPage />
              </RequireSession>
            ),
            loader: feeScheduleLoader,
          },
          {
            index: true,
            path: "fee-schedules",
            element: (
              <RequireSession>
                <FeeSchedulesPage />
              </RequireSession>
            ),
            loader: feeScheduleLoader,
          },
          {
            path: "marketplace/vendor-team-invitation/:invitationId",
            element: (
              <RequireSession>
                <TeamInvitationPage />
              </RequireSession>
            ),
          },
          {
            path: "marketplace/profiles/:tab/:action/:id",
            element: (
              <RequireSession>
                <ManageProfiles />
              </RequireSession>
            ),
            loader: marketplaceProfileLoader,
          },
          {
            index: true,
            path: "marketplace/profiles/:tab/:action",
            element: (
              <RequireSession>
                <ManageProfiles />
              </RequireSession>
            ),
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:tab",
            element: (
              <RequireSession>
                <ManageProfiles />
              </RequireSession>
            ),
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles",
            element: (
              <RequireSession>
                <ManageProfiles />
              </RequireSession>
            ),
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/bookmarks/:tab",
            element: (
              <RequireSession>
                <Bookmarks />
              </RequireSession>
            ),
            loader: bookmarksLoader,
          },
          {
            index: true,
            path: "marketplace/bookmarks",
            element: (
              <RequireSession>
                <Bookmarks />
              </RequireSession>
            ),
            loader: bookmarksLoader,
          },
          {
            index: true,
            path: "marketplace/:type/:id",
            element: (
              <RequireSession>
                <MarketplacePage />
              </RequireSession>
            ),
            loader: marketplaceResultProfileLoader,
          },
          {
            index: true,
            path: "marketplace/:type",
            element: (
              <RequireSession>
                <MarketplacePage />
              </RequireSession>
            ),
            loader: marketplaceResultProfileLoader,
          },
          {
            index: true,
            path: "marketplace",
            element: (
              <RequireSession>
                <MarketplacePage />
              </RequireSession>
            ),
            loader: marketplaceLoader,
          },
          {
            path: "network/:tab/:action/:id",
            element: (
              <RequireSession>
                <Network />
              </RequireSession>
            ),
            loader: networkLoader,
          },
          {
            path: "network/:tab/:action",
            element: (
              <RequireSession>
                <Network />
              </RequireSession>
            ),
            loader: networkLoader,
          },
          {
            path: "network/:tab",
            element: (
              <RequireSession>
                <Network />
              </RequireSession>
            ),
            loader: networkLoader,
          },
          {
            index: true,
            path: "network",
            element: (
              <RequireSession>
                <Network />
              </RequireSession>
            ),
            loader: networkLoader,
          },
          {
            index: true,
            path: "calendar",
            element: (
              <RequireSession>
                <Calendar />
              </RequireSession>
            ),
          },
          {
            path: "administration/:tab/:action/:id",
            element: (
              <RequireSession>
                <EntityAdministration />
              </RequireSession>
            ),
            loader: entityAdministratorLoader,
          },
          {
            path: "administration/:tab/:action",
            element: (
              <RequireSession>
                <EntityAdministration />
              </RequireSession>
            ),
            loader: entityAdministratorLoader,
          },
          {
            path: "administration/:tab",
            element: (
              <RequireSession>
                <EntityAdministration />
              </RequireSession>
            ),
            loader: entityAdministratorLoader,
          },
          {
            index: true,
            path: "administration",
            element: (
              <RequireSession>
                <EntityAdministration />
              </RequireSession>
            ),
            loader: entityAdministratorLoader,
          },
        ],
      },
    ],
  },
  {
    path: "entity-officer-invitation/:invitationId",
    element: <EntityOfficerInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "administrator-invitation/:invitationId",
    element: <AdministratorInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "entity-member-invitation/:invitationId",
    element: <OpenEntityMemberInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "user-member-invitation/:invitationId",
    element: <UserEntityMemberInvitationAcceptancePage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "/",
    element: <Main />,
    errorElement: <ErrorPage />,
    loader: rootLoader,
    children: [
      {
        path: "super-user",
        element: (
          <RequireSession>
            <SuperUserDashboard />
          </RequireSession>
        ),
        loader: superUserLoader,
      },
      {
        path: "super-user/:tab",
        element: (
          <RequireSession>
            <SuperUserDashboard />
          </RequireSession>
        ),
        loader: superUserLoader,
      },
      {
        path: "super-user/:tab/:type/:id",
        element: (
          <RequireSession>
            <SuperUserDashboard />
          </RequireSession>
        ),
        loader: superUserLoader,
      },
      {
        errorElement: <ErrorPage />,
        children: [
          {
            path: "entity-agreements/:id",
            element: (
              <RequireSession>
                <EntityAgreementPage />
              </RequireSession>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "login",
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    element: <Main />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "*",
        element: <ErrorPage error={{ status: 404, statusText: "Page Not Found" }} />,
      },
    ],
  },
]);

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <ReduxProvider store={attorneyHubStore}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <SnackbarProvider maxSnack={3}>
              <SessionProvider>
                <ConfirmDialogProvider>
                  <ImagePreviewDialogProvider>
                    <DialogProvider>
                      <RouterProvider router={router} />
                    </DialogProvider>
                  </ImagePreviewDialogProvider>
                </ConfirmDialogProvider>
              </SessionProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ReduxProvider>
  </React.StrictMode>
);
